import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "@/components/ui/sonner";
import { ResponsiveDialog } from "@/components/common/ResponsiveDialog";
import { api, errorMessage } from "@/lib/api";
import { fmtMoney, todayIso } from "@/lib/format";
import { HANDLING_LABELS } from "@/lib/i18n";

const invalidateInvoicing = (queryClient, basis) => {
  queryClient.invalidateQueries({ queryKey: ["basis", basis.id] });
  queryClient.invalidateQueries({ queryKey: ["bases", basis.order_id] });
  queryClient.invalidateQueries({ queryKey: ["order", basis.order_id] });
  queryClient.invalidateQueries({ queryKey: ["orders"] });
  queryClient.invalidateQueries({ queryKey: ["counts"] });
  queryClient.invalidateQueries({ queryKey: ["invoicing"] });
  queryClient.invalidateQueries({ queryKey: ["history", basis.order_id] });
};

export const MerkFakturertDialog = ({ open, onOpenChange, basis }) => {
  const queryClient = useQueryClient();
  const [reference, setReference] = useState("");
  const [date, setDate] = useState(todayIso());
  const [note, setNote] = useState("");
  const [error, setError] = useState(null);
  useEffect(() => {
    if (open) {
      setReference("");
      setDate(todayIso());
      setNote("");
      setError(null);
    }
  }, [open]);
  const mutation = useMutation({
    mutationFn: async () => (await api.post(`/invoice-bases/${basis.id}/confirm-invoiced`, { invoice_reference: reference.trim(), invoice_date: date, note: note || null })).data,
    onSuccess: (data) => {
      invalidateInvoicing(queryClient, basis);
      toast.success("Merkt som fakturert", { description: `${basis.order_number} rev. ${basis.revision} · faktura ${data.invoice_reference}` });
      onOpenChange(false);
    },
    onError: (err) => setError(errorMessage(err)),
  });
  const valid = reference.trim().length > 0 && /^\d{4}-\d{2}-\d{2}$/.test(date);
  if (!basis) return null;
  return (
    <ResponsiveDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Merk som fakturert"
      description={`${basis.order_number} · revisjon ${basis.revision} · ${fmtMoney(basis.subtotal_excl_vat)} eks. mva. Registreringa er manuell og kan ikkje endrast etterpå.`}
      testId="merk-fakturert-dialog"
      footer={
        <>
          <Button type="button" variant="outline" className="h-11 bg-card md:h-10" onClick={() => onOpenChange(false)} data-testid="merk-fakturert-avbryt">Avbryt</Button>
          <Button type="submit" form="merk-fakturert-form" className="h-11 md:h-10" disabled={!valid || mutation.isPending} data-testid="merk-fakturert-bekreft">{mutation.isPending ? "Lagrar …" : "Merk som fakturert"}</Button>
        </>
      }
    >
      <form id="merk-fakturert-form" onSubmit={(e) => { e.preventDefault(); if (valid) mutation.mutate(); }} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="faktura-ref">Fakturareferanse / fakturanummer *</Label>
          <Input id="faktura-ref" value={reference} onChange={(e) => setReference(e.target.value)} className="h-11 bg-card" autoFocus data-testid="merk-fakturert-referanse" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="faktura-dato">Fakturadato *</Label>
          <Input id="faktura-dato" type="date" value={date} onChange={(e) => setDate(e.target.value)} className="tabular bg-card" data-testid="merk-fakturert-dato" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="faktura-note">Merknad (valfritt)</Label>
          <Textarea id="faktura-note" value={note} onChange={(e) => setNote(e.target.value)} rows={2} className="bg-card" data-testid="merk-fakturert-merknad" />
        </div>
        {error ? <Alert variant="destructive" className="bg-card" data-testid="merk-fakturert-error"><AlertTriangle className="h-4 w-4" /><AlertDescription>{error}</AlertDescription></Alert> : null}
      </form>
    </ResponsiveDialog>
  );
};

export const HandterEksportDialog = ({ open, onOpenChange, exportItem, basis }) => {
  const queryClient = useQueryClient();
  const [handling, setHandling] = useState("trekt_tilbake");
  const [note, setNote] = useState("");
  const [error, setError] = useState(null);
  useEffect(() => {
    if (open) {
      setHandling("trekt_tilbake");
      setNote("");
      setError(null);
    }
  }, [open]);
  const mutation = useMutation({
    mutationFn: async () => (await api.post(`/exports/${exportItem.id}/handle`, { handling, note: note.trim() })).data,
    onSuccess: () => {
      invalidateInvoicing(queryClient, basis);
      toast.success("Eksporten er handtert");
      onOpenChange(false);
    },
    onError: (err) => setError(errorMessage(err)),
  });
  if (!exportItem) return null;
  const valid = note.trim().length >= 3;
  return (
    <ResponsiveDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Handter tidlegare eksport"
      description={`${exportItem.format.toUpperCase()} · revisjon ${exportItem.revision} · eksportert av ${exportItem.exported_by_name}. Dokumenter kva som er gjort med denne eksporten før ny revisjon kan eksporterast.`}
      testId="handter-eksport-dialog"
      footer={
        <>
          <Button type="button" variant="outline" className="h-11 bg-card md:h-10" onClick={() => onOpenChange(false)} data-testid="handter-eksport-avbryt">Avbryt</Button>
          <Button type="submit" form="handter-eksport-form" className="h-11 md:h-10" disabled={!valid || mutation.isPending} data-testid="handter-eksport-bekreft">{mutation.isPending ? "Lagrar …" : "Registrer handtering"}</Button>
        </>
      }
    >
      <form id="handter-eksport-form" onSubmit={(e) => { e.preventDefault(); if (valid) mutation.mutate(); }} className="space-y-4">
        <div className="space-y-2">
          <Label>Kva er gjort?</Label>
          <Select value={handling} onValueChange={setHandling}>
            <SelectTrigger  data-testid="handter-eksport-type"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-popover">
              {Object.entries(HANDLING_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label htmlFor="handter-note">Merknad * (minst 3 teikn)</Label>
          <Textarea id="handter-note" value={note} onChange={(e) => setNote(e.target.value)} rows={3} className="bg-card" autoFocus data-testid="handter-eksport-merknad" />
        </div>
        {error ? <Alert variant="destructive" className="bg-card" data-testid="handter-eksport-error"><AlertTriangle className="h-4 w-4" /><AlertDescription>{error}</AlertDescription></Alert> : null}
      </form>
    </ResponsiveDialog>
  );
};
